export enum ProductType {
  Games = "game",
  GameDLC = "game_dlc",
  DLC = "dlc",
  Wallet = "wallet",
  DigitCode = "digit_code",
  GiftCard = "gift_card",
  FeatureBanner = "feature_banner",
}

export enum ProductStatus {
  Live = "live",
  Unlive = "unlive",
  Upcoming = "upcoming",
  SoldOut = "soldout",
  Duplicate = "duplicate",
}

export default class Product {
  readonly id: string;
  readonly title: string;
  readonly extendedTitle: string | null;
  readonly description: string | null;
  readonly type: ProductType;
  readonly status: ProductStatus;
  readonly poster: string | null;
  readonly genres: string[];
  readonly developers: string[];
  readonly gameId: string | null;
  readonly gameStoreId: string | null;
  readonly currency: string;
  readonly buyPrice: number;
  readonly displayPrice: number;
  readonly buyPriceString: string;
  readonly displayPriceString: string;
  readonly buttonDiscountText: string | null;
  readonly discountPercentage: number;
  readonly availableQuantity: number;
  readonly quantity: number;
  readonly availableRegion: string;
  readonly offerId: string | null;
  readonly hotOffer: boolean;
  readonly isSoldOut: boolean;
  readonly showSteamGC: boolean;
  readonly updatedAt: Date;
  readonly officialWebsite: string | null;
  readonly isAlreadyPurchased: boolean;
  readonly lastPurchasedDate: Date | null;
  readonly isAvailableForUserInRegion: boolean;
  readonly isInCartQuantity: number;
  readonly availableRegionLabel: string;
  readonly availableRegionText: string;
  readonly background: string;
  readonly offerFromStore: {
    id: number;
    icon: string;
    slug: string;
    text: string;
  };
  readonly redirectForSubscription: boolean;
  readonly recommendedOffer: {
    availableQuantity: number;
    availableRegion: string;
    buyPrice: number;
    displayPrice: number;
    offerId: number;
    sellingCurrency: string;
    updatedAt: number;
  };
  readonly cartAddedAt: Date;
  readonly cartItemId: string;
  readonly cartUpdatedAt: Date;
  readonly itemMessage: string;
  readonly similarProducts?: {
    title: string;
    items: any[];
  };
  // Feature banner specific fields
  readonly posterHeroBanner16_9: string | null;
  readonly posterHeroBanner1_1: string | null;
  readonly videoHeroBanner16_9: string | null;
  readonly videoHeroBanner1_1: string | null;
  readonly primaryCta: string | null;
  readonly secondaryCta: string | null;
  readonly redirectTo: string | null;
  readonly secondaryRedirectTo: string | null;

  constructor(data: { [x: string]: any }) {
    this.id = data.product_id;
    this.title = data.title?.replace(/\\'/g, "'");
    this.extendedTitle = data.extended_title ? data.extended_title?.replace(/\\'/g, "'") : null;
    this.description = data.description;
    this.type = data.product_type;
    this.status = data.status;
    this.poster = data.poster;
    this.genres = data.genres;
    this.developers = data.developers || [];
    this.gameId = data.oplay_id;
    this.gameStoreId = data.oplay_store_id;
    this.currency = data.selling_currency;
    this.buyPrice = data.best_buy_price;
    this.displayPrice = data.best_display_price;
    this.buyPriceString = data.best_buy_price_string;
    this.displayPriceString = data.best_display_price_string;
    this.buttonDiscountText = data.button_discount_text;
    this.discountPercentage = data.discount_percentage;
    this.availableQuantity = data.recommended_offer?.available_quantity ?? 0;
    this.quantity = data.quantity;
    this.availableRegion = data.recommended_offer?.available_region ?? "Global";
    this.offerId = data.recommended_offer?.offer_id;
    this.hotOffer = data.hot_offer;
    this.isSoldOut = data.is_soldout;
    this.showSteamGC = data.show_steam_gc_as_main;
    this.officialWebsite = data.official_website;
    this.updatedAt = new Date(data.updated_at * 1000);
    this.isAlreadyPurchased = data.is_already_purchased;
    this.lastPurchasedDate = data.last_purchased_date ? new Date(data.last_purchased_date * 1000) : null;
    this.isAvailableForUserInRegion = data.is_available_for_user_in_region;
    this.isInCartQuantity = data.is_in_cart_quantity;
    this.availableRegionLabel = data.available_region_label;
    this.availableRegionText = data.available_region_text;
    this.background = data.background;
    this.offerFromStore = data.offer_from_store;
    this.redirectForSubscription = data.redirect_for_subscription;
    this.recommendedOffer = data.recommended_offer;
    this.cartAddedAt = new Date(data.cart_added_at * 1000);
    this.cartItemId = data.cart_item_id;
    this.cartUpdatedAt = new Date(data.cart_updated_at * 1000);
    this.itemMessage = data.item_msg || "";
    this.similarProducts = data.similar_products;
    // Feature banner fields
    this.posterHeroBanner16_9 = data.poster_hero_banner_16_9 || null;
    this.posterHeroBanner1_1 = data.poster_hero_banner_1_1 || null;
    this.videoHeroBanner16_9 = data.video_hero_banner_16_9 || null;
    this.videoHeroBanner1_1 = data.video_hero_banner_1_1 || null;
    this.primaryCta = data.primary_cta || null;
    this.secondaryCta = data.secondary_cta || null;
    this.redirectTo = data.redirect_to || null;
    this.secondaryRedirectTo = data.secondary_redirect_to || null;
  }

  copyWith(data: Partial<Product>) {
    return new Product({
      ...this,
      ...data,
    });
  }
}
